import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouteReuseStrategy, RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import * as Sentry from '@sentry/angular';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { environment } from '../environments';
import { AccountComponent } from './account/account.component';
import { AnalyticsService } from './analytics.service';
import { AppRouteReuseStrategy } from './app-route-reuse-strategy';
import { AppComponent } from './app.component';
import { AssignedToDialogComponent } from './assigned-to-dialog/assigned-to-dialog.component';
import { AuthAccountGuard } from './auth-account.guard';
import { AuthUserGuard } from './auth-user.guard';
import { AuthUserService } from './auth-user.service';
import { BillingCancelDialogComponent } from './billing-cancel-dialog/billing-cancel-dialog.component';
import { BillingComponent } from './billing/billing.component';
import { BulkActionButtonComponent } from './bulk-action-button/bulk-action-button.component';
import { ChangeDateDialogComponent } from './change-date-dialog/change-date-dialog.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { ChangeThemeComponent } from './change-theme/change-theme.component';
import { CompletedHistoryComponent } from './completed-history/completed-history.component';
import { ConfirmEmailComponent } from './confirm-email/confirm-email.component';
import { DeactivateGuard } from './deactivate-guard.service';
import { DebugComponent } from './debug/debug.component';
import { DeleteTaskDialogComponent } from './delete-task-dialog/delete-task-dialog.component';
import { FilterSearchComponent } from './filter-search/filter-search.component';
import { FilterTaskButtonsComponent } from './filter-task-buttons/filter-task-buttons.component';
import { FilterTaskTagDialogComponent } from './filter-task-tag-dialog/filter-task-tag-dialog.component';
import { FilterTaskUserDialogComponent } from './filter-task-user-dialog/filter-task-user-dialog.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { GenerateChartComponent } from './generate-chart/generate-chart.component';
import { HelpComponent } from './help/help.component';
import { HomeComponent } from './home/home.component';
import { httpInterceptorProviders } from './http-interceptors';
import { ImportTasksComponent } from './import-tasks/import-tasks.component';
import { InboxArchiveComponent } from './inbox-archive/inbox-archive.component';
import { InboxListItemComponent } from './inbox-list-item/inbox-list-item.component';
import { InboxTaskOutletComponent } from './inbox-task-outlet/inbox-task-outlet.component';
import { InboxThreadOutletComponent } from './inbox-thread-outlet/inbox-thread-outlet.component';
import { InboxComponent } from './inbox/inbox.component';
import { InviteAcceptComponent } from './invite-accept/invite-accept.component';
import { LogInGoogleButtonComponent } from './log-in-google-button/log-in-google-button.component';
import { LoginComponent } from './login/login.component';
import { MenuService } from './menu.service';
import { MessageService } from './message.service';
import { OrgCreateComponent } from './org-create/org-create.component';
import { OrgSettingsComponent } from './org-settings/org-settings.component';
import { OrgUpdateService } from './org-update.service';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { PipelineComponent } from './pipeline/pipeline.component';
import { RegisterEmailComponent } from './register-email/register-email.component';
import { RegisterInviteComponent } from './register-invite/register-invite.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { SearchComponent } from './search/search.component';
import { SettingsService } from './settings.service';
import { SettingsComponent } from './settings/settings.component';
import { SiteStatsChartComponent } from './site-stats-chart/site-stats-chart.component';
import { SpaceCreateDialogComponent } from './space-create-dialog/space-create-dialog.component';
import { SpaceRemoveDialogComponent } from './space-remove-dialog/space-remove-dialog.component';
import { SpaceSettingsComponent } from './space-settings/space-settings.component';
import { StatsService } from './stats.service';
import { SuperAdminAccountDetailComponent } from './super-admin-account-detail/super-admin-account-detail.component';
import { SuperAdminAccountsComponent } from './super-admin-accounts/super-admin-accounts.component';
import { SuperAdminOrgDetailComponent } from './super-admin-org-detail/super-admin-org-detail.component';
import { SuperAdminOrgsComponent } from './super-admin-orgs/super-admin-orgs.component';
import { SuperAdminScriptsComponent } from './super-admin-scripts/super-admin-scripts.component';
import { SuperAdminGuard } from './super-admin.guard';
import { SuperAdminComponent } from './super-admin/super-admin.component';
import { TagDialogComponent } from './tag-dialog/tag-dialog.component';
import { TagService } from './tag.service';
import { TaskDetailPageComponent } from './task-detail-page/task-detail-page.component';
import { TaskDetailComponent } from './task-detail/task-detail.component';
import { TaskInputComponent } from './task-input/task-input.component';
import { TaskListDetailComponent } from './task-list-detail/task-list-detail.component';
import { TaskListComponent } from './task-list/task-list.component';
import { TaskScheduleDialogComponent } from './task-schedule-dialog/task-schedule-dialog.component';
import { TaskService } from './task.service';
import { TaskComponent } from './task/task.component';
import { ThreadEditTitleDialogComponent } from './thread-edit-title-dialog/thread-edit-title-dialog.component';
import { ThreadEditUsersDialogComponent } from './thread-edit-users-dialog/thread-edit-users-dialog.component';
import { DisplayFileUploadComponent } from './thread-file-upload-display/thread-file-upload-display.component';
import { ThreadLeaveDialogComponent } from './thread-leave-dialog/thread-leave-dialog.component';
import { ThreadMessageComponent } from './thread-message/thread-message.component';
import { ThreadRecipientEditorNewComponent } from './thread-recipient-editor-new/thread-recipient-editor-new.component';
import { ThreadTitleUpdateDisplayComponent } from './thread-title-update-display/thread-title-update-display.component';
import { ThreadUserUpdateDisplayComponent } from './thread-user-update-display/thread-user-update-display.component';
import { ThreadViewComponent } from './thread-view/thread-view.component';
import { ThumbnailService } from './thumbnail.service';
import { TimeButtonComponent } from './time-button/time-button.component';
import { TitleService } from './title.service';
import { ToggleTrackingComponent } from './toggle-tracking/toggle-tracking.component';
import { TrashTasksDialogComponent } from './trash-tasks-dialog/trash-tasks-dialog.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { UserAddComponent } from './user-add/user-add.component';
import { UserAdminAddDialogComponent } from './user-admin-add-dialog/user-admin-add-dialog.component';
import { UserAdminRemoveDialogComponent } from './user-admin-remove-dialog/user-admin-remove-dialog.component';
import { UserArchiveDialogComponent } from './user-archive-dialog/user-archive-dialog.component';
import { UserEditComponent } from './user-edit/user-edit.component';
import { WebSocketService } from './web-socket.service';

@NgModule({
  declarations: [
    AppComponent,
    AccountComponent,
    HelpComponent,
    SettingsComponent,
    PageNotFoundComponent,
    LoginComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    ChangePasswordComponent,
    OrgCreateComponent,
    UnauthorizedComponent,
    SiteStatsChartComponent,
    ChangeThemeComponent,
    InboxComponent,
    ThreadViewComponent,
    ThreadMessageComponent,
    ToggleTrackingComponent,
    GenerateChartComponent,
    TimeButtonComponent,
    InboxListItemComponent,
    ThreadEditTitleDialogComponent,
    ThreadEditUsersDialogComponent,
    DisplayFileUploadComponent,
    ThreadRecipientEditorNewComponent,
    UserAddComponent,
    OrgSettingsComponent,
    PipelineComponent,
    TaskListComponent,
    TaskComponent,
    SpaceCreateDialogComponent,
    SpaceSettingsComponent,
    ImportTasksComponent,
    FilterTaskButtonsComponent,
    CompletedHistoryComponent,
    TaskListDetailComponent,
    ConfirmEmailComponent,
    RegisterEmailComponent,
    RegisterInviteComponent,
    InviteAcceptComponent,
    BulkActionButtonComponent,
    TrashTasksDialogComponent,
    AssignedToDialogComponent,
    TagDialogComponent,
    UserEditComponent,
    UserArchiveDialogComponent,
    SpaceRemoveDialogComponent,
    UserAdminAddDialogComponent,
    UserAdminRemoveDialogComponent,
    TaskDetailComponent,
    TaskDetailPageComponent,
    TaskInputComponent,
    ThreadLeaveDialogComponent,
    ChangeDateDialogComponent,
    TaskScheduleDialogComponent,
    FilterSearchComponent,
    SearchComponent,
    InboxThreadOutletComponent,
    InboxTaskOutletComponent,
    ThreadUserUpdateDisplayComponent,
    ThreadTitleUpdateDisplayComponent,
    InboxArchiveComponent,
    FilterTaskUserDialogComponent,
    FilterTaskTagDialogComponent,
    SuperAdminComponent,
    SuperAdminAccountsComponent,
    SuperAdminAccountDetailComponent,
    SuperAdminOrgsComponent,
    SuperAdminOrgDetailComponent,
    BillingComponent,
    HomeComponent,
    BillingCancelDialogComponent,
    DebugComponent,
    DeleteTaskDialogComponent,
    SuperAdminScriptsComponent,
    LogInGoogleButtonComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule.forRoot(
      [
        {
          path: '',
          component: HomeComponent,
          data: { shouldReuseComponent: false, title: '' },
          canActivate: [AuthAccountGuard],
        },
        {
          path: 'help',
          component: HelpComponent,
          data: { shouldReuseComponent: false, title: 'Help' },
        },
        {
          path: 'settings',
          component: SettingsComponent,
          data: { shouldReuseComponent: false, title: 'Settings' },
        },
        {
          path: 'login',
          component: LoginComponent,
          data: { title: 'Login', shouldReuseComponent: false },
        },
        {
          path: 'forgot-password',
          component: ForgotPasswordComponent,
          data: { title: 'Forgot password', shouldReuseComponent: false },
        },
        {
          path: 'change-password',
          component: ChangePasswordComponent,
          data: { title: 'Change password', shouldReuseComponent: false },
        },
        {
          path: 'reset-password',
          component: ResetPasswordComponent,
          data: { title: 'Reset password', shouldReuseComponent: false },
        },
        {
          path: 'register',
          component: RegisterEmailComponent,
          data: { title: 'Register', shouldReuseComponent: false },
        },
        {
          path: 'register-invite',
          component: RegisterInviteComponent,
          data: { title: 'Register', shouldReuseComponent: false },
        },
        {
          path: 'confirm-email',
          component: ConfirmEmailComponent,
          data: { title: 'Confirm email', shouldReuseComponent: false },
        },
        {
          path: 'invite/accept',
          component: InviteAcceptComponent,
          data: { title: 'Accept invites', shouldReuseComponent: false },
        },
        //------------------------------------------
        // Account routes
        //------------------------------------------
        {
          path: 'account',
          component: AccountComponent,
          data: { shouldReuseComponent: false },
          canActivate: [AuthAccountGuard],
        },
        //------------------------------------------
        // Org/User routes
        //------------------------------------------
        {
          path: 'org-create',
          component: OrgCreateComponent,
          data: { shouldReuseComponent: false },
          canActivate: [AuthAccountGuard],
        },
        {
          path: 'org/:orgId',
          component: OrgSettingsComponent,
          data: { shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/user/:userId/edit',
          component: UserEditComponent,
          data: { shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/user-add',
          component: UserAddComponent,
          data: { shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/edit',
          component: SpaceSettingsComponent,
          data: { shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/billing',
          component: BillingComponent,
          data: { shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        //------------------------------------------
        // Inbox routes
        //------------------------------------------
        {
          path: 'org/:orgId/inbox',
          component: InboxComponent,
          data: { title: 'Inbox' },
          canActivate: [AuthUserGuard],
          children: [
            {
              path: 'task/:taskId',
              component: InboxTaskOutletComponent,
              data: { shouldReuseComponent: false },
              canDeactivate: [DeactivateGuard],
            },
            {
              path: 'task/:taskId/thread',
              component: InboxTaskOutletComponent,
              data: { shouldReuseComponent: false, focus: 'thread' },
              canDeactivate: [DeactivateGuard],
            },
            {
              path: ':mode/:threadId',
              component: InboxThreadOutletComponent,
              data: { shouldReuseComponent: false },
              canDeactivate: [DeactivateGuard],
            },
          ],
        },
        {
          path: 'org/:orgId/inbox-archive',
          component: InboxArchiveComponent,
          data: { title: 'Archived Inbox' },
          canActivate: [AuthUserGuard],
          children: [
            {
              path: 'thread/:threadId',
              component: InboxThreadOutletComponent,
              data: { shouldReuseComponent: false, archive: true },
              canDeactivate: [DeactivateGuard],
            },
            {
              path: 'task/:taskId',
              component: InboxTaskOutletComponent,
              data: { shouldReuseComponent: false, archive: true },
              canDeactivate: [DeactivateGuard],
            },
            {
              path: 'task/:taskId/thread',
              component: InboxTaskOutletComponent,
              data: {
                shouldReuseComponent: false,
                focus: 'thread',
                archive: true,
              },
              canDeactivate: [DeactivateGuard],
            },
          ],
        },
        //------------------------------------------
        // Task routes
        //------------------------------------------
        {
          path: 'org/:orgId/task/:taskId',
          component: TaskDetailPageComponent,
          data: { title: 'Task', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/task/:taskId/thread',
          component: TaskDetailPageComponent,
          data: { title: 'Task', shouldReuseComponent: false, focus: 'thread' },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/pipeline',
          component: PipelineComponent,
          data: { title: 'Pipeline', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/pipeline/task/:taskId',
          component: PipelineComponent,
          data: { title: 'Pipeline', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/pipeline/task/:taskId/thread',
          component: PipelineComponent,
          data: { title: 'Pipeline', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/completed-history',
          component: CompletedHistoryComponent,
          data: { title: 'Completed history', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/backlog',
          component: TaskListDetailComponent,
          data: { title: 'Backlog', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/backlog/task/:taskId',
          component: TaskListDetailComponent,
          data: { title: 'Backlog', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/backlog/task/:taskId/thread',
          component: TaskListDetailComponent,
          data: { title: 'Backlog', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/current',
          component: TaskListDetailComponent,
          data: { title: 'Current', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/current/task/:taskId',
          component: TaskListDetailComponent,
          data: { title: 'Current', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/current/task/:taskId/thread',
          component: TaskListDetailComponent,
          data: { title: 'Current', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/trash',
          component: TaskListDetailComponent,
          data: { title: 'Trash', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/trash/task/:taskId',
          component: TaskListDetailComponent,
          data: { title: 'Trash', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        {
          path: 'org/:orgId/space/:spaceId/trash/task/:taskId/thread',
          component: TaskListDetailComponent,
          data: { title: 'Trash', shouldReuseComponent: false },
          canActivate: [AuthUserGuard],
        },
        //------------------------------------------
        // Super admin routes
        //------------------------------------------
        {
          path: 'super-admin',
          component: SuperAdminComponent,
          data: { title: 'Super admin', shouldReuseComponent: false },
          canActivate: [SuperAdminGuard],
        },
        {
          path: 'super-admin/account',
          component: SuperAdminAccountsComponent,
          data: {
            title: 'Super admin - accounts',
            shouldReuseComponent: false,
          },
          canActivate: [SuperAdminGuard],
        },
        {
          path: 'super-admin/account/:accountId',
          component: SuperAdminAccountDetailComponent,
          data: {
            title: 'Super admin - account',
            shouldReuseComponent: false,
          },
          canActivate: [SuperAdminGuard],
        },
        {
          path: 'super-admin/org',
          component: SuperAdminOrgsComponent,
          data: {
            title: 'Super admin - orgs',
            shouldReuseComponent: false,
          },
          canActivate: [SuperAdminGuard],
        },
        {
          path: 'super-admin/org/:orgId',
          component: SuperAdminOrgDetailComponent,
          data: {
            title: 'Super admin - org detail',
            shouldReuseComponent: false,
          },
          canActivate: [SuperAdminGuard],
        },
        {
          path: 'super-admin/stats',
          component: SiteStatsChartComponent,
          data: {
            title: 'Super admin - statistics',
            shouldReuseComponent: false,
          },
          canActivate: [SuperAdminGuard],
        },
        {
          path: 'super-admin/scripts',
          component: SuperAdminScriptsComponent,
          data: { title: 'Super admin - scripts', shouldReuseComponent: false },
          canActivate: [SuperAdminGuard],
        },
        //------------------------------------------
        // Debug
        //------------------------------------------
        {
          path: 'debug',
          component: DebugComponent,
          data: { title: 'Debug', shouldReuseComponent: false },
        },
        //------------------------------------------
        // Error routes
        //------------------------------------------
        {
          path: 'unauthorized',
          component: UnauthorizedComponent,
          data: { title: 'Access denied', shouldReuseComponent: false },
        },
        {
          path: '**',
          component: PageNotFoundComponent,
          data: { title: 'Page not found', shouldReuseComponent: false },
        },
        // Do not place routes after this line
      ],
      {
        anchorScrolling: 'enabled',
        onSameUrlNavigation: 'reload',
        scrollPositionRestoration: 'enabled',
      }
    ),
    BrowserAnimationsModule,
    // Material modules
    MatBadgeModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSnackBarModule,
    MatSortModule,
    MatStepperModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    ScrollingModule,
    DragDropModule,
    // Below is for Progressive Web App (PWA) functionality
    ServiceWorkerModule.register('/ngsw-worker.js', {
      enabled: environment.production,
    }),
    // Start RECAPTCHA
    RecaptchaV3Module,
    CommonModule,
  ],
  providers: [
    httpInterceptorProviders,
    AuthUserService,
    WebSocketService,
    SettingsService,
    StatsService,
    AnalyticsService,
    ThumbnailService,
    OrgUpdateService,
    MenuService,
    TitleService,
    AuthAccountGuard,
    AuthUserGuard,
    MessageService,
    TaskService,
    TagService,
    DeactivateGuard,
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
    {
      provide: ErrorHandler,
      useValue:
        environment.name !== 'local'
          ? Sentry.createErrorHandler({ showDialog: false })
          : new ErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaSiteKey,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
